// libraries.
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// selectors.
import {
  TabsDocumentsReviewOption,
  getTabsDocumentsReview,
  setTabsDocumentsReview,
  getDocuments,
  getSelectedDocument,
  setSelectedDocument,
} from '@domains/documents-review/store/slices/document-review-slice';
import { useDocumentsHistory } from '@domains/documents-review/hooks/useDocumentsHistory';
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';

// styles.
import { WrapperButton } from '../styles';
import { DocumentsHistoryTab } from '@domains/documents-review/components/tabs/DocumentsHistoryTab';
import { DocumentsHistoryLayout } from '@domains/documents-review/components/tabs/DocumentsHistoryLayout';
import { AutocompleteFieldsList } from '@domains/documents-review/components/AutocompleteFieldsList/AutocompleteFieldsList';
import { AutocompleteInfoLayout } from '@domains/documents-review/components/tabs/AutocompleteInfoLayout';

interface DocumentsTabsProps {
  isInModal?: boolean;
  nid?: string;
}

export const DocumentsTabs = ({ isInModal = false, nid }: DocumentsTabsProps) => {
  const dispatch = useDispatch();
  const documents = useSelector(getDocuments);
  const selectedDocument = useSelector(getSelectedDocument);
  const activeTab = useSelector(getTabsDocumentsReview);
  const country = useSelector(getCountry);
  const { isLoading, data: history = [] } = useDocumentsHistory(nid);

  const setActiveTab = (tab: TabsDocumentsReviewOption) => {
    dispatch(setTabsDocumentsReview(tab));

    if (tab === TabsDocumentsReviewOption.AUTOCOMPLETE) {
      dispatch(setSelectedDocument(null));
    }
  };

  const onSelectDocument = (fileUrl: string | null) => {
    dispatch(setSelectedDocument(fileUrl ? fileUrl : null));
    setActiveTab(TabsDocumentsReviewOption.DOCUMENTS);
  };

  useEffect(() => {
    if (!isLoading) {
      const defaultTab =
        history.length > 1
          ? TabsDocumentsReviewOption.HISTORY
          : TabsDocumentsReviewOption.DOCUMENTS;
      dispatch(setTabsDocumentsReview(defaultTab));
    }
  }, [isLoading, history.length, dispatch]);

  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-wrap">
        <DocumentsHistoryTab
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <WrapperButton className="pb-4">
          <button
            className={
              activeTab === TabsDocumentsReviewOption.DOCUMENTS
                ? 'selected-tab'
                : ''
            }
            onClick={() => onSelectDocument(null)}
            disabled={isInModal}
          >
            Documentos
          </button>
        </WrapperButton>
        {!isInModal && country && (
          <WrapperButton className="pb-4">
            <AutocompleteInfoLayout
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </WrapperButton>
        )}
        {activeTab === TabsDocumentsReviewOption.DOCUMENTS &&
          selectedDocument &&
          documents.map(doc => (
            <WrapperButton className="pb-4" key={doc.id}>
              <button
                onClick={() => {
                  if (doc.fileUrl !== selectedDocument) {
                    onSelectDocument(doc.fileUrl);
                  }
                }}
                className={
                  selectedDocument === doc.fileUrl ? 'selected-tab' : ''
                }
                disabled={selectedDocument === doc.fileUrl}
              >
                {doc.title}
              </button>
            </WrapperButton>
          ))}
      </div>
      {(activeTab === TabsDocumentsReviewOption.HISTORY || isLoading) && (
        <DocumentsHistoryLayout />
      )}
      {activeTab === TabsDocumentsReviewOption.AUTOCOMPLETE && (
        <div className="px-3" data-testid="autocomplete-layout">
          <AutocompleteFieldsList />
        </div>
      )}
    </div>
  );
};
